import React from 'react';
import {AlertTriangleIcon, CreditCardIcon, SparklesIcon, UsersIcon} from './icons';
import {QuotaUsage} from '../service/SubscriptionService';

/** Тип квоты AI-запросов — единственная, которую показываем рядом с тарифом. */
const AI_QUOTA_TYPE = 'AI_REQUESTS';

/** Подписи неактивных статусов подписки (зеркало backend `SubscriptionStatus`). */
const INACTIVE_STATUS_SUFFIX: Record<string, string> = {
    EXPIRED: 'истёк',
    CANCELED: 'отменён',
};

export interface PlanBadgeStyle {
    /** Класс тона из дизайн-системы (`index.css`, раздел Status badges). */
    tone: string;
    Icon: React.FC<{className?: string; size?: number}>;
    /** Уточнение после названия тарифа («истёк»), либо `null`. */
    suffix: string | null;
}

/**
 * Тон, иконка и уточнение бейджа по тарифу и статусу подписки.
 *
 * Цвета — только семантические классы Forwolk DS: «фиолетового» токена в системе нет,
 * поэтому FAMILY получает `info`, а не произвольный `purple` (см. plan.md §0.2).
 * Истёкшая или отменённая подписка важнее тарифа: человек должен увидеть, что его
 * права откатились на бесплатные, а не ровный бейдж «Pro».
 */
export function planBadgeStyle(planCode?: string | null, status?: string | null): PlanBadgeStyle {
    const suffix = INACTIVE_STATUS_SUFFIX[(status || '').toUpperCase()] ?? null;
    if (suffix) {
        return {tone: 'badge-warning', Icon: AlertTriangleIcon, suffix};
    }
    switch ((planCode || '').toUpperCase()) {
        case 'PRO':
            return {tone: 'badge-success', Icon: SparklesIcon, suffix: null};
        case 'FAMILY':
            return {tone: 'badge-info', Icon: UsersIcon, suffix: null};
        default:
            // Неизвестный тариф (новый код в каталоге) не должен ломать шапку: нейтральный
            // тон и код как подпись честнее, чем пустое место.
            return {tone: 'badge-neutral', Icon: CreditCardIcon, suffix: null};
    }
}

/**
 * Строка остатка AI-квоты для тарифного блока: «AI: 3 / 50 в месяц» или
 * «AI: без ограничений» при `limit === -1`. Если квоты `AI_REQUESTS` в ответе нет —
 * `null`, и строка не показывается вовсе (а не «AI: 0 / 0»).
 */
export function aiQuotaLine(quotas?: QuotaUsage[] | null): string | null {
    const quota = (quotas || []).find(q => q.quotaType === AI_QUOTA_TYPE);
    if (!quota) {
        return null;
    }
    return quota.limit < 0 ? 'AI: без ограничений' : `AI: ${quota.used} / ${quota.limit} в месяц`;
}

interface Props {
    planCode?: string | null;
    planName?: string | null;
    /** `ACTIVE` / `EXPIRED` / `CANCELED` (`SubscriptionStatus`). */
    status?: string | null;
    /** Данные ещё грузятся — показываем skeleton той же высоты, без скачка раскладки. */
    loading?: boolean;
    /** Данные недоступны — бейджа нет (решение о заглушке принимает экран). */
    error?: boolean;
    className?: string;
}

/**
 * Бейдж тарифного плана (CTHS-340). Компактная метка «иконка + текст + цвет» по
 * styleguide (*Status badge — always color + icon + text, never color alone*).
 * Переиспользуется шапкой (`UserAvatar`), профилем и админ-панелью подписок.
 */
const PlanBadge: React.FC<Props> = ({planCode, planName, status, loading, error, className = ''}) => {
    if (loading) {
        return (
            <span
                data-testid="plan-badge-skeleton"
                aria-hidden="true"
                className={`inline-block h-5 w-20 rounded-full bg-gray-100 dark:bg-white/10 animate-pulse ${className}`}
            />
        );
    }

    const label = (planName || '').trim() || (planCode || '').trim().toUpperCase();
    if (error || !label) {
        return null;
    }

    const {tone, Icon, suffix} = planBadgeStyle(planCode, status);
    return (
        <span
            data-testid="plan-badge"
            data-plan={(planCode || '').toUpperCase()}
            className={`badge ${tone} ${className}`}
        >
            <Icon className="w-3.5 h-3.5" size={14}/>
            {suffix ? `${label} · ${suffix}` : label}
        </span>
    );
};

export default PlanBadge;
